import {
  attachValidation,
  EditableAccessRule,
  PersistedAccessRule,
  ValidationError,
} from "../../models";
import {
  areDatesEqual,
  areTimesEqual,
  toValidDate,
  toValidTime,
} from "../ruleEditorTimeUtils";

export interface EffectiveDateRangeAccessRule extends PersistedAccessRule {
  effectiveDateStart: string;
  effectiveDateEnd: string;
  effectiveTimeSlotStart?: string;
  effectiveTimeSlotEnd?: string;
}

export interface EditableEffectiveDateRangeAccessRule extends EditableAccessRule {
  effectiveDateStart: Date | null;
  effectiveDateEnd: Date | null;
  effectiveTimeSlotStart: Date | null;
  effectiveTimeSlotEnd: Date | null;
}

export const effectiveDateRangeDiffAdapter = {
  // Effective date range rules cannot be deleted, they can only be created or updated unless all rules for
  // an entitlement context (Entitlement Type + Selling Season + Fulfillment Season + Location) are deleted.
  isDeleted: (rule: EditableEffectiveDateRangeAccessRule) => false,

  isUpdated: (
    original: EffectiveDateRangeAccessRule,
    current: EditableEffectiveDateRangeAccessRule,
  ) =>
    !areDatesEqual(original.effectiveDateStart, current.effectiveDateStart) ||
    !areDatesEqual(original.effectiveDateEnd, current.effectiveDateEnd) ||
    !areTimesEqual(
      original.effectiveTimeSlotStart,
      current.effectiveTimeSlotStart,
    ) ||
    !areTimesEqual(original.effectiveTimeSlotEnd, current.effectiveTimeSlotEnd),
};

export const toEditableEffectiveDateRange = (
  rule: EffectiveDateRangeAccessRule,
): EditableEffectiveDateRangeAccessRule =>
  attachValidation(
    {
      ...rule,
      localRuleId: crypto.randomUUID(),
      isNew: false,
      effectiveDateStart: toValidDate(rule.effectiveDateStart),
      effectiveDateEnd: toValidDate(rule.effectiveDateEnd),
      effectiveTimeSlotStart: toValidTime(rule.effectiveTimeSlotStart),
      effectiveTimeSlotEnd: toValidTime(rule.effectiveTimeSlotEnd),
    },
    validate,
  );

export const createEmptyEditableEffectiveDateRangeRule = (
  overrides?: Partial<EditableEffectiveDateRangeAccessRule>,
): EditableEffectiveDateRangeAccessRule =>
  attachValidation(
    {
      localRuleId: crypto.randomUUID(),
      isNew: true,
      liftAccessRuleId: null,
      effectiveDateStart: null,
      effectiveDateEnd: null,
      effectiveTimeSlotStart: null,
      effectiveTimeSlotEnd: null,
      ...overrides,
    },
    validate,
  );

export function validate(
  rule: EditableEffectiveDateRangeAccessRule,
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (
    rule.effectiveDateStart == null ||
    rule.effectiveDateEnd == null ||
    rule.effectiveTimeSlotStart == null ||
    rule.effectiveTimeSlotEnd == null
  ) {
    errors.push({
      messageKey:
        "ACCESS_RULE_VALIDATION_EFFECTIVE_DATE_REQUIRED_FIELD_MISSING",
    });
  } else if (
    rule.effectiveDateStart != null &&
    rule.effectiveDateEnd != null &&
    rule.effectiveDateStart > rule.effectiveDateEnd
  ) {
    errors.push({
      messageKey:
        "ACCESS_RULE_VALIDATION_EFFECTIVE_DATE_RANGE_DATE_START_BEFORE_END",
    });
  } else if (
    rule.effectiveTimeSlotStart != null &&
    rule.effectiveTimeSlotEnd != null &&
    rule.effectiveTimeSlotStart > rule.effectiveTimeSlotEnd
  ) {
    errors.push({
      messageKey:
        "ACCESS_RULE_VALIDATION_EFFECTIVE_DATE_RANGE_TIME_START_BEFORE_END",
    });
  }

  return errors;
}
